import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import { Order, OrderStatus } from '../../entities/order/Order'
import GotIt from '../got-it/GotItContainer'

import { styled } from '@mui/material/styles'
import messages from './ChangeOrderStatusButton.messages'
import { PageLoader } from '../PageLoader/PageLoader'

export interface IOwnProps {
    order: Order
}
export interface IStateProps {}

export interface IDispatchProps {
    requestOrderStatusChange: (
        orderId: number,
        nextStatus: string,
        onEnd?: () => void
    ) => void
}

export type IProps = IOwnProps & IStateProps & IDispatchProps

const OrderStatusButton = styled(Button)({
    fontSize: 14,
    fontWeight: 400,
    padding: '5px 7px',
    borderRadius: 5,
    marginLeft: 5,
})

const AwaitingPoButton = styled(OrderStatusButton)(({ theme }) => ({
    fontWeight: 700,
    fontSize: 15,
    color: theme.palette.warning.dark,
    borderColor: theme.palette.warning.main,
    borderWidth: 2,
    '&.Mui-disabled': {
        color: theme.palette.warning.dark,
        borderColor: theme.palette.warning.main,
        borderWidth: 2,
        opacity: 0.9,
    },
}))

const AwaitingPoWrapper = styled('span')({
    position: 'relative',
    display: 'inline-block',
})

const AwaitingPoHelperText = styled('span')(({ theme }) => ({
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 5,
    fontSize: 10,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
}))

const ChangeOrderStatusButton: React.FC<IProps> = (props) => {
    const { order, requestOrderStatusChange } = props
    const [loading, setLoading] = useState(false)

    const intl = useIntl()

    const handleMoveToReady = () => {
        setLoading(true)
        requestOrderStatusChange(
            order.orderId,
            OrderStatus.READY_FOR_PICKUP,
            () => setLoading(false)
        )
    }

    const isAwaitingFinalPo = !order.hasFinalPo()

    return (
        <>
            <PageLoader open={loading} />
            {order.canMoveToInProgress() && (
                <OrderStatusButton
                    aria-label={intl.formatMessage({
                        ...messages.toInProgressButton,
                    })}
                    variant='outlined'
                    color='warning'
                    onClick={() => {
                        setLoading(true)
                        requestOrderStatusChange(
                            order.orderId,
                            OrderStatus.ACCEPTED,
                            () => setLoading(false)
                        )
                    }}
                >
                    <FormattedMessage {...messages.toInProgressButton} />
                </OrderStatusButton>
            )}

            {!order.canRemoveFromPallet() && order.isInProgress() && (
                isAwaitingFinalPo ? (
                    <AwaitingPoWrapper>
                        <AwaitingPoButton
                            aria-label={intl.formatMessage({
                                ...messages.awaitingFinalPo,
                            })}
                            variant='outlined'
                            disabled
                            startIcon={<HourglassEmptyIcon fontSize='small' />}
                        >
                            <FormattedMessage {...messages.awaitingFinalPo} />
                        </AwaitingPoButton>
                        <AwaitingPoHelperText>
                            <FormattedMessage {...messages.awaitingFinalPoTooltip} />
                        </AwaitingPoHelperText>
                    </AwaitingPoWrapper>
                ) : (
                    <OrderStatusButton
                        aria-label={intl.formatMessage({
                            ...messages.moveReadyButton,
                        })}
                        variant='outlined'
                        color='warning'
                        onClick={handleMoveToReady}
                    >
                        <FormattedMessage {...messages.moveReadyButton} />
                    </OrderStatusButton>
                )
            )}

            {order.canRemoveFromPallet() && (
                <OrderStatusButton
                    aria-label={intl.formatMessage({
                        ...messages.removeButton,
                    })}
                    variant='outlined'
                    color='warning'
                    onClick={() => {
                        setLoading(true)

                        requestOrderStatusChange(
                            order.orderId,
                            OrderStatus.CANCELED,
                            () => setLoading(false)
                        )
                    }}
                >
                    <FormattedMessage {...messages.removeButton} />
                </OrderStatusButton>
            )}

            {order.isCanceled &&
                !order.canRemoveFromPallet() &&
                !order.wasMovedToInProgress && (
                    <GotIt orderId={order.orderId} />
                )}

            {order.wasMovedToInProgress && (
                <GotIt
                    reason={intl.formatMessage({ ...messages.reason })}
                    orderId={order.orderId}
                    customHandler={() => {
                        setLoading(true)
                        requestOrderStatusChange(
                            order.orderId,
                            OrderStatus.MOVED_TO_IN_PROGRESS,
                            () => setLoading(false)
                        )
                    }}
                />
            )}
        </>
    )
}

export default ChangeOrderStatusButton
